import React, { useEffect, useState } from "react";
import { Container, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";

import Page from "src/components/Page";
import Header from "./Header";
import MperitoCard from "src/components/MperitoCard";
import SummarySection from "./components/SummarySection";
import TodayListBox from "./components/TodayListBox";

import axios from "src/utils/axios";

import { fetchUserData } from "src/actions/accountActions";
import { fetchCategory } from "src/actions/mgmtSelectValueAction";
import { fetchProductDetailsForDashboard } from "src/actions/mgmtProductAction";
import { fetchOrders } from "src/actions/orderActions";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: "100%",
    paddingTop: theme.spacing(3),
    paddingBottom: 100,
  },
}));

function Dashboard() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const [usersToday, setUsersToday] = useState([]);
  const [productsToday, setProductsToday] = useState([]);
  const [categoriesToday, setCategoriesToday] = useState([]);
  const [ordersToday, setOrdersToday] = useState([]);
  const [failedOrdersToday, setFailedOrdersToday] = useState([]);

  const [usersTotal, setUsersTotal] = useState(0);
  const [productsTotal, setProductsTotal] = useState(0);
  const [categoriesTotal, setCategoriesTotal] = useState(0);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [failedOrdersTotal, setFailedOrdersTotal] = useState(0);

  const businessInfo = useSelector((state) => state?.app?.businessInfo);
  const customerId = businessInfo?.customerId;



  useEffect(() => {
    if (!customerId) return;

    dispatch(fetchUserData(customerId));
    dispatch(fetchCategory(customerId));
    dispatch(fetchOrders(customerId));
    dispatch(fetchProductDetailsForDashboard(customerId, customerId, "User"));
  }, [customerId, dispatch]);



  const fetchUsersAddedToday = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        "/api/auth/admin/all/users/added/today"
      );

      setUsersToday(
        Array.isArray(res?.data?.data?.usersForToday)
          ? res.data.data.usersForToday
          : []
      );
      setUsersTotal(res?.data?.data?.grandTotal || 0);
    } catch {
      setUsersToday([]);
      setUsersTotal(0);
    } finally {
      setLoading(false);
    }
  };


  const fetchProductsAddedToday = async () => {
  try {
    setLoading(true);

    const res = await axios.get(
      "/api/admin/catalog/products/added/today"
    );

    const productsArray = res?.data?.data?.productsForToday;

    setProductsToday(
      Array.isArray(productsArray) ? productsArray : []
    );

    setProductsTotal(res?.data?.data?.grandTotal || 0);
  } catch {
    setProductsToday([]);
    setProductsTotal(0);
  } finally {
    setLoading(false);
  }
};


  const fetchCategoriesAddedToday = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        "/api/admin/catalog/category/added/today"
      );

      setCategoriesToday(
        Array.isArray(res?.data?.data?.categoriesForToday)
          ? res.data.data.categoriesForToday
          : []
      );
      setCategoriesTotal(res?.data?.data?.grandTotal || 0);
    } catch {
      setCategoriesToday([]);
      setCategoriesTotal(0);
    } finally {
      setLoading(false);
    }
  };


  const fetchOrdersPlacedToday = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        "/api/admin/order/added/today"
      );

      setOrdersToday(
        Array.isArray(res?.data?.data?.ordersForToday)
          ? res.data.data.ordersForToday
          : []
      );
      setOrdersTotal(res?.data?.data?.grandTotal || 0);
    } catch {
      setOrdersToday([]);
      setOrdersTotal(0);
    } finally {
      setLoading(false);
    }
  };


  const fetchFailedOrdersToday = async () => {
    try {
      const res = await axios.get(
        "/api/admin/order/added/today?status=FAILED"
      );

      setFailedOrdersToday(
        Array.isArray(res?.data?.data?.ordersForToday)
          ? res.data.data.ordersForToday
          : []
      );
      setFailedOrdersTotal(res?.data?.data?.grandTotal || 0);
    } catch {
      setFailedOrdersToday([]);
      setFailedOrdersTotal(0);
    }
  };



  useEffect(() => {
    fetchUsersAddedToday();
    fetchProductsAddedToday();
    fetchCategoriesAddedToday();
    fetchOrdersPlacedToday();
    fetchFailedOrdersToday();
  }, []);



  const usersList = usersToday.map((b) => ({
    label: b.businessName,
    count: b.totalUsers,
  }));

  const productsList = productsToday.map((b) => ({
    label: b.businessName,
    count: b.totalProducts,
  }));

  const categoriesList = categoriesToday.map((b) => ({
    label: b.businessName,
    count: b.totalCategories,
  }));

  const ordersList = ordersToday.map((b) => ({
    label: b.businessName,
    count: b.totalOrders,
  }));

  const failedOrdersList = failedOrdersToday.map((b) => ({
    label: b.businessName,
    count: b.totalOrders,
  }));



  return (
    <Page className={classes.root} title="Mperito Dashboard">
      <Container maxWidth={false}>
        <Header />

        <MperitoCard title="TODAY'S MPERITO DASHBOARD" />

        {/* TOP 5 CARDS (USING grandTotal) */}
        <SummarySection
          todayUsersCount={usersTotal}
          todayProductsCount={productsTotal}
          todayCategoriesCount={categoriesTotal}
          todayOrdersCount={ordersTotal}
          todayFailedOrdersCount={failedOrdersTotal}
        />


        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} md={6}>
            <TodayListBox
              title="Orders by Business"
              items={ordersList}
              loading={loading}
              disabled={ordersTotal === 0}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TodayListBox
              title="Failed Orders by Business"
              items={failedOrdersList}
              loading={loading}
              disabled={failedOrdersTotal === 0}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TodayListBox
              title="Users Added Today"
              items={usersList}
              loading={loading}
              disabled={usersTotal === 0}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            {/* Reserved for Today Trend Graph */}
            <TodayListBox
              title="New Products Added"
              items={productsList}
              loading={loading}
              disabled={productsTotal === 0}
            />

          </Grid>

          <Grid item xs={12} md={6}>
            <TodayListBox
              title="New Categories by Business"
              items={categoriesList}
              loading={loading}
              disabled={categoriesTotal === 0}
              minHeight={120}
            />
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}

export default Dashboard;
